{
  "close": "Close",
  "phase": {
    "agent_running": "Running",
    "awaiting_user_input": "Waiting for you",
    "idle_active": "Idle",
    "turn_budget_exhausted": "Budget reached",
    "paused": "Paused"
  },
  "taskStatus": {
    "todo": "To do",
    "progress": "In progress",
    "done": "Done",
    "blocked": "Blocked",
    "failed": "Failed"
  },
  "composer": {
    "placeholder": "Message the team…",
    "placeholderPaused": "Message the team to start the room…"
  },
  "rooms": {
    "title": "Rooms",
    "description": "Rooms where a team of agents works together on your goal, with you in the loop.",
    "new": "New room",
    "search": "Search rooms…",
    "open": "Open",
    "rename": "Rename",
    "duplicate": "Duplicate",
    "delete": "Delete room",
    "deleteTitle": "Delete this room?",
    "deleteDescription": "“{{title}}” and its transcript, tasks and files are deleted permanently. Running turns are cancelled.",
    "deleted": "Room deleted",
    "created": "Room created",
    "duplicated": "Created “{{title}}” (paused)",
    "untitled": "Room #{{id}}",
    "noTeam": "No team",
    "allTeams": "All teams",
    "allStatuses": "All statuses",
    "filterTeam": "Filter by team",
    "filterStatus": "Filter by status",
    "emptyTitle": "No rooms yet",
    "emptyDescription": "Start a room from a team and give it a goal; the team leader plans and delegates the work.",
    "emptyShort": "No rooms yet",
    "noMatches": "No rooms match “{{query}}”",
    "columns": {
      "room": "Room",
      "status": "Status",
      "turns": "Turns",
      "activity": "Last activity",
      "created": "Created"
    }
  },
  "newRoom": {
    "title": "New room",
    "description": "Pick a team; its members join the room as workers. The room starts paused until you send the first message.",
    "submit": "Create room",
    "team": "Team",
    "teamHint": "Teams need exactly one leader and a model for every member.",
    "teamPlaceholder": "Select a team…",
    "members_one": "{{count}} member",
    "members_other": "{{count}} members",
    "notReady": "Not ready",
    "name": "Name",
    "namePlaceholder": "e.g. Q3 market research",
    "noTeamsTitle": "No teams yet",
    "noTeamsManage": "Create a team with a leader first, then start rooms from it.",
    "noTeamsAsk": "Ask an administrator to create a team before starting a room.",
    "openTeams": "Open teams"
  },
  "room": {
    "title": "Room",
    "nameLabel": "Room name",
    "rename": "Rename room",
    "actions": "Room actions",
    "panel": "Room details",
    "showPanel": "Show details",
    "hidePanel": "Hide details",
    "reconnecting": "Connection lost — reconnecting…",
    "turns_one": "{{count}} turn",
    "turns_other": "{{count}} turns",
    "turnsOf_one": "{{count}} of {{budget}} turns",
    "turnsOf_other": "{{count}} of {{budget}} turns",
    "tabs": {
      "tasks": "Tasks",
      "plan": "Plan",
      "team": "Team",
      "files": "Files",
      "search": "Search"
    },
    "state": {
      "paused": "The room is paused. Send a message or resume to continue.",
      "budget": "Turn budget reached ({{used}} of {{budget}}). Raise it in the settings to continue.",
      "running_one": "{{names}} is working…",
      "running_other": "{{names}} are working…",
      "runningUnknown": "The team is getting to work…",
      "yourTurn": "The team is waiting for your reply.",
      "idle": "The team is idle. Send a message or step the leader."
    },
    "control": {
      "pause": "Pause",
      "resume": "Resume",
      "step": "Step: let the leader take a turn",
      "stepRequested": "The leader takes a turn",
      "stop": "Stop all activity",
      "stopTitle": "Stop the room?",
      "stopDescription": "Running turns are cancelled, queued work is dropped and the room is paused.",
      "stopped": "Room stopped",
      "cancel": "Cancel running turns",
      "cancelTitle": "Cancel running turns?",
      "cancelDescription": "Workers that are thinking stop now; the room itself keeps running.",
      "cancelled_one": "Cancelled {{count}} turn",
      "cancelled_other": "Cancelled {{count}} turns"
    },
    "export": {
      "copy": "Copy as Markdown",
      "download": "Download as Markdown",
      "copied": "Transcript copied"
    },
    "memory": {
      "title": "Memory",
      "compress": "Compress memory",
      "compressed": "Older messages were summarized",
      "nothingToCompress": "Nothing to compress yet",
      "summary": "Rolling summary",
      "noSummary": "No summary yet. Older messages are summarized once the room passes {{count}} messages.",
      "disabled": "Compression is off: agents only see the most recent messages."
    },
    "tasks": {
      "emptyTitle": "No tasks yet",
      "emptyDescription": "The leader creates tasks and assigns them to team members as the work is planned.",
      "progress": "{{done}} of {{total}} done",
      "unassigned": "Unassigned",
      "created": "Created",
      "started": "Started",
      "completed": "Completed",
      "description": "Description",
      "instructions": "Instructions",
      "expectedOutput": "Expected output",
      "error": "Error",
      "resultSummary": "Result",
      "result": "Result data",
      "finishedToast": "{{name}} finished “{{title}}”",
      "failedToast": "{{name}} could not finish “{{title}}”"
    },
    "context": {
      "plan": "Plan",
      "planEmpty": "The leader writes the plan here once the work is broken down.",
      "planUpdates_one": "{{count}} plan update",
      "planUpdates_other": "{{count}} plan updates",
      "notes": "Shared notes",
      "addPlaceholder": "Add a note for the team…",
      "addHint": "Every agent sees these notes in its context.",
      "add": "Add note",
      "added": "Note added",
      "text": "Note text",
      "delete": "Delete note",
      "deleteTitle": "Delete this note?",
      "deleteDescription": "Agents no longer see it in their context.",
      "deleted": "Note deleted",
      "byAgent": "by an agent",
      "byPerson": "by a person",
      "guardrails": "Guardrails",
      "guardrailsEmpty": "No room rules. Add them in the room settings.",
      "category": {
        "goal": "Goal",
        "guardrail": "Guardrail",
        "data_source": "Data source",
        "discussion": "Discussion",
        "task_status": "Plan",
        "other": "Other"
      }
    },
    "workers": {
      "fromTeam": "Members of {{team}}",
      "noTeam": "The team was removed; the roster is kept.",
      "editTeam": "Edit team",
      "empty": "No workers in this room",
      "leader": "Leader",
      "nextUp": "Next up",
      "agent": "Agent",
      "simple": "Simple member",
      "member": "A member",
      "formerMember": "Former member",
      "role": "Role",
      "workingOn": "Working on {{title}}",
      "status": {
        "thinking": "Thinking",
        "idle": "Idle",
        "despawned": "Removed"
      },
      "despawn": "Remove {{name}} from the room",
      "despawnConfirm": "Remove from room",
      "despawnTitle": "Remove {{name}}?",
      "despawnDescription": "The worker stops, its open tasks are handed back to the leader and it no longer takes turns in this room. The team itself is unchanged.",
      "despawned": "{{name}} was removed from the room",
      "despawnedTitle_one": "Removed ({{count}})",
      "despawnedTitle_other": "Removed ({{count}})"
    },
    "files": {
      "upload": "Upload",
      "newText": "New text file",
      "newTextDescription": "Agents can read and edit text files in this room.",
      "editDescription": "Saving with an existing name replaces that file.",
      "readOnlyDescription": "Extracted text (read-only). Download the file for the original.",
      "filename": "File name",
      "content": "Content",
      "emptyTitle": "No files yet",
      "emptyDescription": "Files you upload here — and files the agents write — are shared with the whole team.",
      "uploaded": "Uploaded {{name}}",
      "saved": "Saved {{name}}",
      "deleted": "Deleted {{name}}",
      "download": "Download {{name}}",
      "downloadShort": "Download",
      "delete": "Delete {{name}}",
      "deleteTitle": "Delete this file?",
      "deleteDescription": "{{name}} is removed for the whole team.",
      "deleteConfirm": "Delete file",
      "status": {
        "pending": "Processing",
        "failed": "Extraction failed"
      }
    },
    "search": {
      "placeholder": "Search room data and files…",
      "scope": "Search in",
      "scopes": {
        "all": "All",
        "data": "Data",
        "files": "Files"
      },
      "noResults": "Nothing matches “{{query}}”",
      "dataMeta": "{{chars}} chars",
      "fileMeta": "File",
      "dataTitle_one": "Room data ({{count}} key)",
      "dataTitle_other": "Room data ({{count}} keys)",
      "dataEmpty": "Agents haven't stored shared data yet.",
      "browseFiles": "Browse files"
    },
    "data": {
      "add": "Add",
      "addTitle": "New room data entry",
      "description": "Shared text the team reads and writes with its room data tools. Values are stored as plain text (JSON is kept verbatim).",
      "key": "Key",
      "value": "Value",
      "valuePlaceholder": "Text, Markdown or JSON",
      "edit": "Edit {{key}}",
      "delete": "Delete {{key}}",
      "deleteTitle": "Delete this entry?",
      "deleteDescription": "“{{key}}” is removed from the room data. Agents can no longer read it.",
      "deleteConfirm": "Delete entry",
      "saved": "Saved “{{key}}”",
      "deleted": "Deleted “{{key}}”",
      "issues": {
        "keyRequired": "Enter a key.",
        "keyTooLong": "Keys can be at most {{keyMax}} characters.",
        "keyExists": "This key already exists. Edit that entry instead.",
        "valueTooLong": "Values can be at most {{valueMax}} characters.",
        "tooManyKeys": "A room can hold at most {{keysMax}} keys.",
        "totalTooLarge": "All values together can be at most {{totalMax}} characters."
      }
    },
    "settings": {
      "title": "Room settings",
      "description": "Changes apply from the next turn.",
      "saved": "Room settings saved",
      "name": "Name",
      "instructions": "Room instructions",
      "instructionsHint": "Added to every worker's instructions in this room, on top of the team setup.",
      "humanGate": "Human in the loop",
      "hitlEnabled": "Ask me before continuing",
      "hitlEnabledHint": "The leader can hand the turn back to you when it needs input or approval.",
      "hitlCondition": "When to ask",
      "hitlConditionHint": "Optional, e.g. “before sending anything to a customer”.",
      "budgets": "Limits",
      "turnBudget": "Turn budget",
      "turnBudgetHint_one": "The room stops after this many worker turns ({{count}} used). Empty means no limit.",
      "turnBudgetHint_other": "The room stops after this many worker turns ({{count}} used). Empty means no limit.",
      "unlimited": "Unlimited",
      "turnsUnit": "turns",
      "concurrency": "Parallel workers",
      "concurrencyHint": "Members that may work at the same time (1–16).",
      "passBudget": "Leader passes per run",
      "passBudgetHint": "Safety limit on consecutive leader passes (1–200).",
      "contextMax": "Messages in context",
      "contextMaxHint": "How many recent messages agents see (10–500).",
      "messagesUnit": "messages",
      "compression": "Summarize older messages",
      "compressionHint": "Keeps long rooms coherent by folding older messages into a rolling summary.",
      "compressionKb": "Also store summaries in the knowledge base",
      "compressionKbHint": "Lets agents look up earlier parts of the conversation.",
      "guardrails": "Rules",
      "guardrailsHint": "One rule per line (max. 64). Every worker must follow them.",
      "sentry": "Sentry organization",
      "sentryHint": "Optional slug for Sentry tools used in this room."
    }
  },
  "transcript": {
    "label": "Room conversation",
    "scrollToEnd": "Scroll to latest",
    "loadEarlier_one": "Load {{count}} earlier message",
    "loadEarlier_other": "Load earlier messages ({{count}} more)",
    "emptyTitle": "Give the team a goal",
    "empty": "Describe what you want done. The leader plans the work and delegates it to the team.",
    "emptyPaused": "Describe what you want done — your first message starts the room.",
    "you": "You",
    "system": "System",
    "attachments": "Attachments",
    "to": "to",
    "copy": "Copy message",
    "reasoning": "Reasoning",
    "thinking": "Thinking…",
    "working": "{{name}} is working",
    "savedData": "Saved to room data: {{keys}}"
  }
}
